import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import { Bar } from 'react-chartjs-2';

import { getXAxisConfig } from '~features/dashboard-v3/charts';
import { MainChartLegend, ChartCard } from '~components/Charts';
import { Colors } from '~library/prerenderTheme';
import { numberFormatter } from '~library/numbers';
import ChartContainer, { type CDNAnalyticsChartContainerProps } from '../CDNAnalyticsChartContainer';
import buildVolumeDatasets from './buildVolumeDatasets';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackCDNAnalyticsEvent from '../useTrackCDNAnalyticsEvent';
import { useIsPrinting } from '~library/print';

export const CHART_ID = 'cdn-analytics-volume-chart';
export const VOLUME_VISIBILITY_CHARTS: ChartConfig[] = [{ id: CHART_ID, type: 'dataset' }];
export const TOOLTIP_MESSAGE =
  'Number of crawler visits. A HIT represents a requested page served from cache. A MISS represents a requested page rendered on the fly.';

function CDNAnalyticsVolumeChart({ rawData }: { rawData: AggregatedMetrics.PagesCrawledVolumeResult }) {
  const track = useTrackCDNAnalyticsEvent();
  const ChartItemsVisibility = useChartItemsVisibility(VOLUME_VISIBILITY_CHARTS);
  const { isPrinting } = useIsPrinting();
  const datasets = React.useMemo(() => buildVolumeDatasets(rawData), [rawData]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'crawler visits - volume',
      dataset: datasets[datasetIndex].label,
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(datasets.length);
  }, [datasets]);

  return (
    <ChartCard.Content>
      <MainChartLegend>
        <MainChartLegend.Label
          label="Cache hits"
          labelColor={Colors.Mint}
          value={numberFormatter.format(rawData.total_cache_hit)}
          crossedOut={!ChartItemsVisibility.itemsVisibility[0]}
          onClick={() => onLegendItemClicked(0)}
        />
        <MainChartLegend.Label
          label="Cache misses"
          labelColor={Colors.Gold}
          value={numberFormatter.format(rawData.total_cache_miss)}
          crossedOut={!ChartItemsVisibility.itemsVisibility[1]}
          onClick={() => onLegendItemClicked(1)}
        />
      </MainChartLegend>

      <ChartCard.ChartLayout>
        <Bar
          id={CHART_ID}
          data={{ labels: rawData.intervals, datasets }}
          options={{
            animation: isPrinting ? false : undefined,
            scales: {
              x: getXAxisConfig(rawData.granularity),
              y: {
                type: 'linear',
                ticks: {
                  count: 5,
                  callback: (value) => numberFormatter.format(Number(value)),
                },
              },
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => `${context.dataset.label}: ${numberFormatter.format(Number(context.parsed.y))}`,
                },
              },
            },
          }}
        />
      </ChartCard.ChartLayout>
    </ChartCard.Content>
  );
}

const MemoizedCDNAnalyticsVolumeChart = React.memo(CDNAnalyticsVolumeChart);

type ContainerProps = CDNAnalyticsChartContainerProps & {
  rawData?: AggregatedMetrics.PagesCrawledVolumeResult;
};
function Container({ isLoading, rawData, ...props }: ContainerProps) {
  const hasDataForTimeRange = !!rawData && !!rawData.data.length;

  return (
    <ChartContainer {...props} hasDataForTimeRange={hasDataForTimeRange} isLoading={isLoading}>
      {rawData && <MemoizedCDNAnalyticsVolumeChart rawData={rawData} />}
    </ChartContainer>
  );
}

export default Container;
